/**
 * Roomgrid sidebar (222px, collapsible to a 64px icon rail).
 *
 * Structure per the Roomgrid handoff:
 *  - Brand block: 26px teal tile + "Notify" + mono "ADMIN" label.
 *  - Flat nav groups with uppercase micro headers (no accordions):
 *      Accounts · Test tools · Credit consumption · Administration
 *  - Active item: sunken fill, weight 600, teal icon and a 2.5px teal
 *    rail on the left edge.
 *  - Footer: user chip opening the full user menu popover
 *    (see SidebarUserChip).
 *
 * The Settings sub-items live in the user chip popover, not here.
 */

import React from "react";
import { useTranslate, useSidebarState } from "react-admin";
import { Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Business as AccountsIcon,
  MailOutline as EmailIcon,
  SmsOutlined as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Send as TelegramIcon,
  NotificationsNone as WebPushIcon,
  PhoneIphone as AppPushIcon,
  ForumOutlined as TeamChatIcon,
  SmartToyOutlined as ViberIcon,
  ChatBubbleOutline as LineIcon,
  InboxOutlined as InboxIcon,
  WebhookOutlined as WebhookIcon,
  GridViewOutlined as OverviewIcon,
  AccountTreeOutlined as CostCenterIcon,
  SellOutlined as PricingIcon,
  ListAltOutlined as TransactionsIcon,
  TimelineOutlined as EventLogsIcon,
  PeopleOutlined as UserManagementIcon,
  AdminPanelSettingsOutlined as PlatformUsersIcon,
  ScheduleOutlined as ScheduledJobsIcon,
  BarChartOutlined as StatisticsIcon,
  DescriptionOutlined as NotificationLogsIcon,
  NotificationsActiveOutlined as BrandIcon,
  ExpandMore as ExpandIcon,
} from "@mui/icons-material";
import { SidebarUserChip } from "./SidebarUserChip";
import { useIsSuperUser } from "@/utils/permissions";

interface NavItem {
  to: string;
  textKey: string;
  fallback: string;
  icon: React.ReactNode;
  /** Exact match only (for group landing routes shadowed by children). */
  exact?: boolean;
  /** Solo SuperUser lo ve (y solo SuperUser puede usar su API). */
  superUserOnly?: boolean;
}

interface NavGroup {
  headerKey?: string;
  headerFallback?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      {
        to: "/apps/accounts",
        textKey: "resources.accounts.name",
        fallback: "Accounts",
        icon: <AccountsIcon />,
      },
    ],
  },
  {
    headerKey: "resources.test.name",
    headerFallback: "Test tools",
    items: [
      { to: "/test/email", textKey: "resources.test.submenu.email", fallback: "Email", icon: <EmailIcon /> },
      { to: "/test/sms", textKey: "resources.test.submenu.sms", fallback: "SMS", icon: <SmsIcon /> },
      { to: "/test/whatsapp", textKey: "resources.test.submenu.whatsapp", fallback: "WhatsApp", icon: <WhatsAppIcon /> },
      { to: "/test/telegram", textKey: "resources.test.submenu.telegram", fallback: "Telegram", icon: <TelegramIcon /> },
      { to: "/test/web-push", textKey: "resources.test.submenu.webPush", fallback: "Web Push", icon: <WebPushIcon /> },
      { to: "/test/app-push", textKey: "resources.test.submenu.appPush", fallback: "App Push", icon: <AppPushIcon /> },
      { to: "/test/team-chat", textKey: "resources.test.submenu.teamChat", fallback: "Team chat", icon: <TeamChatIcon /> },
      { to: "/test/viber", textKey: "resources.test.submenu.viber", fallback: "Viber", icon: <ViberIcon /> },
      { to: "/test/line", textKey: "resources.test.submenu.line", fallback: "LINE", icon: <LineIcon /> },
      { to: "/test/inbox", textKey: "resources.test.submenu.inbox", fallback: "Inbox", icon: <InboxIcon /> },
      { to: "/test/webhook", textKey: "resources.test.submenu.webhook", fallback: "Webhook", icon: <WebhookIcon /> },
    ],
  },
  {
    headerKey: "resources.credit-consumption.name",
    headerFallback: "Credit consumption",
    items: [
      { to: "/credit-consumption", textKey: "menu.overview", fallback: "Overview", icon: <OverviewIcon />, exact: true },
      { to: "/credit-consumption/cost-center", textKey: "resources.credit-consumption.submenu.costCenter", fallback: "Cost Centers", icon: <CostCenterIcon /> },
      { to: "/credit-consumption/pricing", textKey: "resources.credit-consumption.submenu.pricing", fallback: "Pricing", icon: <PricingIcon /> },
      { to: "/credit-consumption/credit-transaction", textKey: "resources.credit-consumption.submenu.creditTransaction", fallback: "Transactions", icon: <TransactionsIcon /> },
      { to: "/credit-consumption/credit-event-logs", textKey: "resources.credit-consumption.submenu.creditEventLogs", fallback: "Event Logs", icon: <EventLogsIcon /> },
    ],
  },
  {
    headerKey: "menu.groups.administration",
    headerFallback: "Administration",
    items: [
      { to: "/user-management", textKey: "resources.user-management.name", fallback: "User Management", icon: <UserManagementIcon /> },
      { to: "/platform-users", textKey: "resources.platform-users.name", fallback: "Admin users", icon: <PlatformUsersIcon />, superUserOnly: true },
      { to: "/scheduled-jobs", textKey: "resources.scheduled-jobs.name", fallback: "Scheduled Jobs", icon: <ScheduledJobsIcon /> },
      { to: "/statistics", textKey: "resources.statistics.name", fallback: "Statistics", icon: <StatisticsIcon /> },
      { to: "/notification-logs", textKey: "resources.notification-logs.name", fallback: "Notification Logs", icon: <NotificationLogsIcon /> },
    ],
  },
];

export const CustomMenu: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const layout = theme.custom.layout;
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useSidebarState();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isSuperUser = useIsSuperUser();

  // Un grupo que se queda sin items visibles no pinta ni su cabecera.
  const navGroups = React.useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.superUserOnly || isSuperUser),
      })).filter((group) => group.items.length > 0),
    [isSuperUser],
  );

  const expanded = isSidebarOpen || isSmallScreen;

  const isActive = (item: NavItem): boolean =>
    item.exact
      ? location.pathname === item.to
      : location.pathname === item.to ||
        location.pathname.startsWith(`${item.to}/`);

  // Collapsible groups (only the ones with a header). Default: open. A group is
  // force-open while it contains the active route so the current page is never
  // hidden behind a collapsed header.
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  const toggleGroup = (key: string): void =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleNavigate = (to: string): void => {
    navigate(to);
    if (isSmallScreen) setSidebarOpen(false);
  };

  const width = isSmallScreen
    ? "100%"
    : expanded
    ? layout.sidebarWidth
    : layout.sidebarRailWidth;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Fills the sidebar column under the 56px topbar (the drawer paper
        // provides the actual viewport height on mobile).
        height: "100%",
        width,
        minWidth: isSmallScreen ? "auto" : width,
        maxWidth: isSmallScreen ? "100%" : width,
        backgroundColor: rg.bg,
        borderRight: isSmallScreen ? "none" : `1px solid ${rg.border}`,
        transition:
          "width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, background-color 0.25s ease",
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "9px",
          p: expanded ? "16px 14px 12px" : "16px 0 12px",
          justifyContent: expanded ? "flex-start" : "center",
          flex: "none",
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            flex: "none",
            borderRadius: "7px",
            backgroundColor: rg.accent,
            color: "#ffffff",
            display: "grid",
            placeItems: "center",
          }}
        >
          <BrandIcon sx={{ fontSize: 16 }} />
        </Box>
        {expanded && (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <Typography
              sx={{
                fontSize: "14.5px",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              Notify
            </Typography>
            <Typography
              className="rg-mono"
              sx={{
                fontSize: "9.5px",
                letterSpacing: "0.08em",
                color: rg.text3,
                lineHeight: 1,
              }}
            >
              ADMIN
            </Typography>
          </Box>
        )}
      </Box>

      {/* Nav */}
      <Box
        component="nav"
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: expanded ? "2px 10px 10px" : "2px 8px 10px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        {navGroups.map((group, gi) => {
          const groupKey = group.headerKey ?? String(gi);
          // A collapsible header exists only for groups that have one, and only
          // when the sidebar is expanded (the rail has no room for headers).
          const collapsible = Boolean(group.headerKey) && expanded;
          const hasActive = group.items.some((it) => isActive(it));
          // Force-open when the active route lives inside this group.
          const isOpen = !collapsible || hasActive || !collapsed[groupKey];
          return (
          <Box
            key={groupKey}
            sx={{ display: "flex", flexDirection: "column", gap: "1px" }}
          >
            {group.headerKey && expanded && (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => toggleGroup(groupKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") toggleGroup(groupKey);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  borderRadius: "6px",
                  p: "0 8px 5px 10px",
                  userSelect: "none",
                  "&:hover .rg-group-chevron": { color: rg.text2 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontWeight: 600,
                    color: rg.text3,
                  }}
                >
                  {translate(group.headerKey, {
                    _: group.headerFallback,
                    smart_count: 1,
                  })}
                </Typography>
                <ExpandIcon
                  className="rg-group-chevron"
                  sx={{
                    fontSize: 15,
                    color: rg.text3,
                    transition: "transform 0.18s ease",
                    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
              </Box>
            )}
            {isOpen && group.items.map((item) => {
              const active = isActive(item);
              const label = translate(item.textKey, {
                _: item.fallback,
                smart_count: 1,
              });
              const button = (
                <Box
                  key={item.to}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNavigate(item.to)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNavigate(item.to);
                  }}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    height: 32,
                    px: expanded ? "10px" : 0,
                    justifyContent: expanded ? "flex-start" : "center",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: active ? rg.bg2 : "transparent",
                    color: active ? rg.text : rg.text2,
                    fontWeight: active ? 600 : 400,
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    "&:hover": { backgroundColor: rg.bg2 },
                    // Teal 2.5px rail on the active item.
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 7,
                      bottom: 7,
                      width: "2.5px",
                      borderRadius: "2px",
                      backgroundColor: active ? rg.accent : "transparent",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 17,
                      color: active ? rg.accent : rg.text3,
                      flex: "none",
                    },
                  }}
                >
                  {item.icon}
                  {expanded && (
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {label}
                    </Box>
                  )}
                </Box>
              );
              return expanded ? (
                button
              ) : (
                <Tooltip key={item.to} title={label} placement="right">
                  {button}
                </Tooltip>
              );
            })}
          </Box>
          );
        })}
      </Box>

      {/* User chip footer */}
      <SidebarUserChip collapsed={!expanded} />
    </Box>
  );
};

export default CustomMenu;
